import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Download, Radio, WifiOff } from 'lucide-react';
import { api, get } from '@/lib/api';
import { subscribe, getSocket } from '@/lib/socket';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusPill } from '@/components/ui/status-pill';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime, todayIsoDate } from '@/lib/format';
import type { Visitor, VisitKind, VisitorStatus } from '@hooddesk/shared-types';

interface VisitorListResponse {
  items: Visitor[];
  next_cursor?: string | null;
}

const KINDS: VisitKind[] = ['guest', 'delivery', 'cab', 'service', 'staff'];

const FLASH_TTL_MS = 1800;

// URL-synced filter+sort+page state for visitor logs. We DROP the single-day
// `date` filter in favour of a from/to range — the share-link UX needs an
// explicit range to round-trip cleanly. Default `from` = today (preserves the
// V0.2 default behaviour of "today only" until the user widens the range).
const VISITOR_FILTER_DEFAULTS = {
  search: '',
  status: '',
  visit_kind: '',
  unit_id: '',
  date_from: todayIsoDate(),
  date_to: todayIsoDate(),
  sort: 'created_desc',
  page_size: 50
};

interface BlockOption {
  id: string;
  name: string;
}
interface UnitOption {
  id: string;
  block_id: string | null;
  number: string;
}

export function VisitorLogsPage(): JSX.Element {
  const [filterState, setFilterState] = useUrlSearchState(VISITOR_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const [flashIds, setFlashIds] = useState<Record<string, number>>({});
  const [liveConnected, setLiveConnected] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Debounce free-text search → URL state.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Fetch units + blocks for the unit_id filter chip. Light query — cached.
  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: BlockOption[] }>('/society/blocks')
  });
  const unitsQuery = useQuery({
    queryKey: ['society', 'units', 'all-visitors'],
    queryFn: () => get<{ items: UnitOption[] }>('/society/units', { params: { limit: 200 } })
  });
  const blockNameById = useMemo(() => {
    const map = new Map<string, string>();
    (blocksQuery.data?.items ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [blocksQuery.data]);
  const unitOptions = useMemo(() => {
    const units = unitsQuery.data?.items ?? [];
    return [...units]
      .map((u) => ({
        id: u.id,
        label: `${u.block_id ? blockNameById.get(u.block_id) ?? '?' : '—'} / ${u.number}`,
        sortKey: `${u.block_id ? blockNameById.get(u.block_id) ?? 'zzz' : 'zzz'}-${u.number}`
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));
  }, [unitsQuery.data, blockNameById]);

  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.status,
        filterState.visit_kind,
        filterState.unit_id,
        filterState.date_from,
        filterState.date_to,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.date_from) params.from = `${filterState.date_from}T00:00:00.000Z`;
    if (filterState.date_to) params.to = `${filterState.date_to}T23:59:59.999Z`;
    if (filterState.status) params.status = filterState.status;
    if (filterState.visit_kind) params.visit_kind = filterState.visit_kind;
    if (filterState.unit_id) params.unit_id = filterState.unit_id;
    if (filterState.search) params.q = filterState.search;
    return params;
  }, [filterState]);

  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const queryKey = ['visitor-logs', queryParams, cursor] as const;

  const query = useQuery({
    queryKey,
    queryFn: () =>
      get<VisitorListResponse>('/visitors', {
        params: { ...queryParams, ...(cursor ? { cursor } : {}) }
      }),
    // When realtime socket is offline, poll every 30s so the page still updates.
    // When connected, rely on socket invalidations (no polling).
    refetchInterval: liveConnected ? false : 30_000,
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Visitor>(query.data, cursor, filtersHash);

  // Realtime: any visitor state change → invalidate + flash the row
  const pendingFlashes = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handlers = (['visitor:awaiting', 'visitor:approved', 'visitor:denied'] as const).map(
      (eventName) =>
        subscribe(eventName, (e) => {
          if ('visitor_id' in e && e.visitor_id) {
            pendingFlashes.current.add(e.visitor_id);
          }
          void queryClient.invalidateQueries({ queryKey });
        })
    );
    return () => handlers.forEach((unsub) => unsub());
    // re-subscribe on filter change so we always invalidate the active query
  }, [queryClient, queryParams]);

  // Track socket connection state independently of event traffic so the
  // "Live" badge + polling fallback reflect real connectivity, not just
  // "did we recently see a visitor event".
  useEffect(() => {
    const s = getSocket();
    const onConnect = (): void => setLiveConnected(true);
    const onDisconnect = (): void => setLiveConnected(false);
    setLiveConnected(s.connected);
    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onDisconnect);
    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('connect_error', onDisconnect);
    };
  }, []);

  // After data refetch, surface any pending flashes
  useEffect(() => {
    if (!query.data || pendingFlashes.current.size === 0) return;
    const now = Date.now();
    const next: Record<string, number> = {};
    pendingFlashes.current.forEach((id) => {
      next[id] = now;
    });
    pendingFlashes.current.clear();
    setFlashIds((prev) => ({ ...prev, ...next }));
    const timer = window.setTimeout(() => {
      setFlashIds((prev) => {
        const cutoff = Date.now() - FLASH_TTL_MS;
        return Object.fromEntries(Object.entries(prev).filter(([, t]) => t > cutoff));
      });
    }, FLASH_TTL_MS + 50);
    return () => window.clearTimeout(timer);
  }, [query.data]);

  // Server-side filtering now applies `visit_kind` directly — no client filter needed.
  const data = useMemo(() => paginator.items, [paginator.items]);

  // ─── Server-side CSV export ────────────────────────────────────────────────
  // Hits GET /v1/visitors/export.csv with filters (date range / status /
  // visit_kind) and triggers a browser download via a temporary Blob URL.
  // Uses axios so the Bearer token is attached + 401-refresh interceptor runs.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [exportTo, setExportTo] = useState<string>(todayIsoDate());
  const [exportStatus, setExportStatus] = useState<VisitorStatus | ''>('');
  const [exportKind, setExportKind] = useState<VisitKind | ''>('');
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function runExport(): Promise<void> {
    setExportBusy(true);
    setExportError(null);
    try {
      const params: Record<string, string> = {
        from: `${exportFrom}T00:00:00.000Z`,
        to: `${exportTo}T23:59:59.999Z`
      };
      if (exportStatus) params.status = exportStatus;
      if (exportKind) params.visit_kind = exportKind;

      const res = await api.get<Blob>('/visitors/export.csv', {
        params,
        responseType: 'blob'
      });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hooddesk-visitors-${exportFrom}-to-${exportTo}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      // Server returns JSON on EXPORT_TOO_LARGE even though we asked for a blob —
      // parse the blob back into JSON to surface the friendly message.
      let msg = 'Export failed';
      const e = err as { response?: { data?: Blob | { error?: { message?: string } } } };
      const payload = e.response?.data;
      if (payload instanceof Blob) {
        try {
          const text = await payload.text();
          const json = JSON.parse(text) as { error?: { message?: string } };
          if (json.error?.message) msg = json.error.message;
        } catch {
          /* keep default */
        }
      } else if (payload && 'error' in payload && payload.error?.message) {
        msg = payload.error.message;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setExportError(msg);
    } finally {
      setExportBusy(false);
    }
  }

  const visitorSortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Created (newest)' },
      { value: 'created_asc', label: 'Created (oldest)' },
      { value: 'expected_at_asc', label: 'Expected (earliest)' },
      { value: 'expected_at_desc', label: 'Expected (latest)' },
      { value: 'checked_in_desc', label: 'Checked in (latest)' }
    ],
    []
  );

  const visitorFilterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        value: filterState.status,
        options: [
          { value: '', label: 'All statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'checked_in', label: 'Checked in' },
          { value: 'checked_out', label: 'Checked out' },
          { value: 'denied', label: 'Denied' },
          { value: 'expired', label: 'Expired' }
        ],
        onChange: (v) => setFilterState({ status: v })
      },
      {
        id: 'visit_kind',
        label: 'Type',
        type: 'select',
        value: filterState.visit_kind,
        options: [
          { value: '', label: 'All types' },
          ...KINDS.map((k) => ({ value: k, label: k }))
        ],
        onChange: (v) => setFilterState({ visit_kind: v })
      },
      {
        id: 'date',
        label: 'Date range',
        type: 'date_range',
        value: { from: filterState.date_from, to: filterState.date_to },
        onChange: (v) => setFilterState({ date_from: v.from, date_to: v.to })
      },
      {
        id: 'unit_id',
        label: 'Unit',
        type: 'select',
        value: filterState.unit_id,
        options: [
          { value: '', label: 'All units' },
          ...unitOptions.map((u) => ({ value: u.id, label: u.label }))
        ],
        onChange: (v) => setFilterState({ unit_id: v })
      }
    ],
    [filterState, unitOptions, setFilterState]
  );

  const visitorHasActiveFilters =
    filterState.search !== VISITOR_FILTER_DEFAULTS.search ||
    filterState.status !== VISITOR_FILTER_DEFAULTS.status ||
    filterState.visit_kind !== VISITOR_FILTER_DEFAULTS.visit_kind ||
    filterState.unit_id !== VISITOR_FILTER_DEFAULTS.unit_id ||
    filterState.date_from !== VISITOR_FILTER_DEFAULTS.date_from ||
    filterState.date_to !== VISITOR_FILTER_DEFAULTS.date_to ||
    filterState.sort !== VISITOR_FILTER_DEFAULTS.sort ||
    filterState.page_size !== VISITOR_FILTER_DEFAULTS.page_size;

  const resetVisitorFilters = () => {
    setFilterState(VISITOR_FILTER_DEFAULTS);
    setSearchInput('');
  };

  return (
    <>
      <PageHeader
        title="Visitor logs"
        description="Audit trail of every visitor event — searchable, exportable, retained per society policy."
        actions={
          <div className="flex items-center gap-vt-3">
            <span
              className="inline-flex items-center gap-1.5 rounded-vt-pill border border-vt-border px-2.5 py-1 text-vt-12 font-medium text-vt-text-muted"
              title={liveConnected ? 'Subscribed to realtime visitor events' : 'Waiting for realtime connection…'}
            >
              <Radio className="h-3.5 w-3.5" strokeWidth={2} />
              <span
                className={
                  liveConnected
                    ? 'vt-live-dot inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--vt-safe)]'
                    : 'inline-block h-1.5 w-1.5 rounded-full bg-vt-border-strong'
                }
              />
              <span>{liveConnected ? 'Live' : 'Idle'}</span>
            </span>
            <Button variant="secondary" size="sm" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] px-vt-6 pb-vt-8 pt-vt-4 space-y-vt-4">
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search visitor name or phone…"
          filters={visitorFilterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: visitorSortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetVisitorFilters}
          hasActiveFilters={visitorHasActiveFilters}
          resultLabel={`${data.length} record${data.length === 1 ? '' : 's'}`}
        />

        {!liveConnected ? (
          <div className="flex items-center gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-2 text-vt-12 text-vt-text-muted">
            <WifiOff className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Realtime offline — refreshing every 30s instead.</span>
          </div>
        ) : null}

        <Card>
          <CardContent className="p-vt-4">
            {query.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : query.isError ? (
              <EmptyState
                icon={<ClipboardList className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load visitor logs"
                description={(query.error as Error)?.message ?? 'Try again later.'}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : data.length === 0 ? (
              <EmptyState
                icon={<ClipboardList className="h-6 w-6" strokeWidth={1.75} />}
                title="No visitors match your filters"
                description="Try widening the date range, clearing filters, or searching for a different name/phone."
                action={
                  visitorHasActiveFilters ? (
                    <Button variant="secondary" onClick={resetVisitorFilters}>
                      Reset filters
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Visitor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Checked in</TableHead>
                    <TableHead>Checked out</TableHead>
                    <TableHead>Vehicle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((v) => (
                    <TableRow key={v.id} className={flashIds[v.id] ? 'vt-row-flash' : undefined}>
                      <TableCell>
                        <div className="flex items-center gap-vt-3">
                          {v.photo_url ? (
                            <img
                              src={v.photo_url}
                              alt={v.visitor_name}
                              className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-vt-border"
                              loading="lazy"
                              onError={(e) => {
                                // Fallback to initials if image fails to load
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <span
                            className="h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary"
                            style={{ display: v.photo_url ? 'none' : 'flex' }}
                          >
                            {v.visitor_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-vt-text">{v.visitor_name}</div>
                            <div className="truncate text-vt-12 text-vt-text-muted">{v.phone}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize text-vt-text-muted">{v.visit_kind}</TableCell>
                      <TableCell><StatusPill status={v.status} /></TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(v.expected_at)}</TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(v.checked_in_at)}</TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(v.checked_out_at)}</TableCell>
                      <TableCell className="font-mono text-vt-13 text-vt-text-muted">{v.vehicle_no ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(paginator.nextCursor)}
                loading={query.isFetching}
                itemsShown={data.length}
                pageSize={filterState.page_size}
                onLoadMore={() => {
                  if (paginator.nextCursor) setCursor(paginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Server-side CSV export dialog — hits GET /v1/visitors/export.csv */}
      <Dialog
        open={exportOpen}
        onOpenChange={(o) => {
          if (!exportBusy) setExportOpen(o);
          if (!o) setExportError(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export visitor logs</DialogTitle>
            <DialogDescription>
              Download a CSV of visitor events in the selected window. Limit 10,000 rows — tighten filters if the export exceeds that.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-vt-3 py-vt-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="export-from">From</Label>
              <Input
                id="export-from"
                type="date"
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="export-to">To</Label>
              <Input
                id="export-to"
                type="date"
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="export-status">Status</Label>
              <Select
                id="export-status"
                value={exportStatus}
                onChange={(e) => setExportStatus(e.target.value as VisitorStatus | '')}
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="checked_in">Checked in</option>
                <option value="checked_out">Checked out</option>
                <option value="denied">Denied</option>
                <option value="expired">Expired</option>
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="export-kind">Type</Label>
              <Select
                id="export-kind"
                value={exportKind}
                onChange={(e) => setExportKind(e.target.value as VisitKind | '')}
              >
                <option value="">All types</option>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {exportError ? (
            <div className="rounded-vt-sm border border-[color:var(--vt-danger)]/40 bg-[color:var(--vt-danger)]/10 px-vt-3 py-vt-2 text-vt-12 text-[color:var(--vt-danger)]">
              {exportError}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportOpen(false)} disabled={exportBusy}>
              Cancel
            </Button>
            <Button onClick={() => void runExport()} disabled={exportBusy}>
              <Download className="h-4 w-4" /> {exportBusy ? 'Exporting…' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
